  if (!customElements.get('nwp-chat-launchpoint')) {
    class NwpChatLaunchpoint extends HTMLElement {
      static boundApis = new WeakSet();
      static panelSelector = '.header-sidebar, .nwp-header-popout, .nwp-search-dialog, cart-drawer';

      connectedCallback() {
        if (this.reparenting) return;
        document.querySelectorAll('nwp-chat-launchpoint').forEach(other => {
          if (other !== this && other.parentElement === document.body) { this.expanded = other.expanded; other.remove(); }
        });
        if (this.parentElement !== document.body) { document.body.append(this); return; }
        this.trigger = this.querySelector('.nwp-chat-launchpoint__button');
        this.notice = this.querySelector('.nwp-chat-launchpoint__notice');
        this.status = this.querySelector('[data-chat-status]');
        if (!this.trigger || !this.notice || !this.status) return;
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.trigger.setAttribute('role', 'button');
        this.setExpanded(Boolean(this.expanded));
        this.dataset.chatEnhanced = 'true';
        this.trigger.addEventListener('click', event => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (this.pending) return;
          this.bindApi();
          if (this.expanded) {
            this.closeChat();
            return;
          }
          this.requestOpen(this.trigger);
        }, options);
        this.trigger.addEventListener('keydown', event => {
          if (event.key === ' ') { event.preventDefault(); this.trigger.click(); }
        }, options);
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && !this.notice.hidden) {
            this.settle();
            this.notice.hidden = true;
            this.trigger.focus({ preventScroll: true });
          }
        }, options);
        document.addEventListener('tidioChat-ready', () => this.onReady(), options);
        window.addEventListener('pageshow', () => this.onReady(), options);
        document.addEventListener('shopify:section:unload', event => {
          if (String(event.detail?.sectionId) === this.dataset.ownerSection || event.target.id === 'shopify-section-' + this.dataset.ownerSection) this.remove();
        }, options);
        // The persistent entry point remains keyboard reachable inside native
        // drawer/search focus traps. Opening chat first closes those surfaces.
        document.addEventListener('dialog:before-show', event => {
          const panel = event.target;
          if (!panel.matches?.(NwpChatLaunchpoint.panelSelector)) return;
          panel.focusTrap?.updateContainerElements([panel, this]);
          if (this.expanded) this.closeChat();
          this.settle();
          this.notice.hidden = true;
        }, { ...options, capture: true });
        document.addEventListener('dialog:after-hide', event => {
          const panel = event.target;
          if (panel.matches?.(NwpChatLaunchpoint.panelSelector)) panel.focusTrap?.updateContainerElements(panel);
        }, { ...options, capture: true });
        document.querySelectorAll(NwpChatLaunchpoint.panelSelector).forEach(panel => {
          if (panel.hasAttribute('open')) panel.focusTrap?.updateContainerElements([panel, this]);
        });
        // Tidio can restore an open conversation without emitting an opening
        // event. Follow its own launcher state, including late widget mounts.
        this.hostObserver = new window.MutationObserver(() => this.observeProvider());
        this.hostObserver.observe(document.body, { childList: true });
        this.bindApi();
        document.dispatchEvent(new Event('nwp:chat-launchpoint-change'));
      }

      disconnectedCallback() {
        if (this.reparenting || this.isConnected) return;
        this.modal = null;
        this.listeners?.abort();
        this.hostObserver?.disconnect();
        this.providerObserver?.disconnect();
        this.bodyLockObserver?.disconnect();
        this.bodyLockObserver = null;
        this.providerRoot = null;
        this.settle();
        delete this.dataset.chatEnhanced;
        document.querySelectorAll(NwpChatLaunchpoint.panelSelector).forEach(panel => {
          if (panel.hasAttribute('open')) panel.focusTrap?.updateContainerElements(panel);
        });
        document.dispatchEvent(new Event('nwp:chat-launchpoint-change'));
      }

      // A native modal makes body siblings inert regardless of z-index. Keep
      // this same controller inside its top layer, preserving provider state.
      moveTo(container) {
        this.reparenting = true;
        try { container.append(this); }
        finally { this.reparenting = false; }
      }

      enterModal(dialog) {
        if (!dialog?.open || !this.isConnected) return;
        if (this.expanded) {
          this.closeChat();
        }
        this.settle();
        this.notice.hidden = true;
        this.modal = dialog;
        this.moveTo(dialog);
      }

      leaveModal(dialog) {
        if (this.modal !== dialog) return;
        this.modal = null;
        this.moveTo(document.body);
      }

      bindApi() {
        const api = window.tidioChatApi;
        this.observeProvider();
        if (this.api !== api) {
          this.api = api;
          this.syncProvider();
        }
        if (!api || typeof api.on !== 'function' || NwpChatLaunchpoint.boundApis.has(api)) return;
        NwpChatLaunchpoint.boundApis.add(api);
        // Bind once per provider API, not per theme-editor section instance.
        const current = () => {
          const launcher = document.getElementById('NwpChatLaunchpoint');
          return window.tidioChatApi === api && launcher?.dataset.chatEnhanced === 'true' ? launcher : null;
        };
        // Readiness comes from tidioChat-ready, or the status check in openChat
        // when it already fired. Do not register an API ready callback here:
        // Tidio invokes it synchronously and it can open before these listeners
        // exist, losing the open event and leaving the close button unreachable.
        // Completed-animation events lag behind the visible intent. Track the
        // provider's transition starts so a quick next click reverses it.
        api.on('beforeOpen', () => {
          const launcher = current();
          if (!launcher) return;
          launcher.prepareBodyLock();
          launcher.setExpanded(true);
          launcher.settle();
          launcher.notice.hidden = true;
        });
        api.on('beforeClose', () => current()?.setExpanded(false));
        api.on('close', () => current()?.finishBodyLock());
      }

      prepareBodyLock() {
        if (this.bodyLockObserver) return;
        const body = document.body;
        // Tidio's iOS fullscreen lock fixes the body at top:0. Our inset chat
        // leaves the page visible, so retain its visual offset and full height.
        // Observe only this provider lock; its own cleanup still restores the
        // original inline styles and document scroll position on close.
        this.lockedScrollY = body.style.position === 'fixed' && parseFloat(body.style.top) < 0
          ? -parseFloat(body.style.top) : window.scrollY;
        this.bodyLockObserver = new window.MutationObserver(() => {
          if (body.style.position === 'fixed' && body.style.overflow === 'hidden'
            && body.style.width === '100%' && body.style.top === '0px' && body.style.height === '100%') {
            this.bodyWasLocked = true;
            body.style.top = `${-this.lockedScrollY}px`;
            body.style.height = 'auto';
          } else if (this.bodyWasLocked && body.style.position !== 'fixed') {
            this.finishBodyLock();
          }
        });
        this.bodyLockObserver.observe(body, { attributes: true, attributeFilter: ['style'] });
      }

      finishBodyLock() {
        // beforeClose precedes the provider's exit animation. Keep the offset
        // until its body lock has actually been removed, including reversals.
        if (this.expanded || document.body.style.position === 'fixed') return;
        this.bodyLockObserver?.disconnect();
        this.bodyLockObserver = null;
        this.bodyWasLocked = false;
      }

      observeProvider() {
        const root = document.getElementById('tidio-chat')?.shadowRoot;
        if (root === this.providerRoot) return;
        this.providerObserver?.disconnect();
        this.providerRoot = root;
        if (!root) return;
        // The same chat-open/chat-closed contract controls Tidio's native icon
        // and our existing theme styles. Read it; never mutate provider state.
        this.providerObserver = new window.MutationObserver(records => {
          if (records.some(record => record.type === 'childList' || record.target.id === 'button')) this.syncProvider();
        });
        this.providerObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        this.syncProvider();
      }

      syncProvider() {
        const button = this.providerRoot?.getElementById('button');
        if (!button) return;
        const open = button.classList.contains('chat-open');
        if (!open && !button.classList.contains('chat-closed')) return;
        this.setExpanded(open);
        if (open) { this.settle(); this.notice.hidden = true; }
      }

      onReady() {
        this.bindApi();
        this.syncProvider();
        if (this.pending && !this.closingPanels && this.openChat()) {
          this.settle();
          this.notice.hidden = true;
        }
      }

      async requestOpen(trigger) {
        if (this.pending || !this.isConnected) return;
        // A Studio can dock this same launcher in its purchase bar. Let the
        // owning surface save its draft and release its focus/scroll lock before
        // opening the provider, just as native drawers do below.
        this.dispatchEvent(new Event('nwp:chat-before-open', { bubbles: true }));
        if (this.modal) {
          const dialog = this.modal;
          dialog.close('nwp-chat');
          this.leaveModal(dialog);
        }
        this.pending = true;
        this.pendingTrigger = trigger;
        this.pendingLabel = trigger.querySelector('[data-chat-label]');
        this.originalLabel = this.pendingLabel?.textContent;
        trigger.setAttribute('aria-busy', 'true');
        this.trigger.setAttribute('aria-busy', 'true');
        this.trigger.setAttribute('aria-label', this.trigger.dataset.loadingLabel);
        if (this.pendingLabel) this.pendingLabel.textContent = this.trigger.dataset.loadingLabel;
        this.status.textContent = this.trigger.dataset.loadingLabel;
        // Keep the normal loading state on the button; reserve the contact
        // notice for a genuine initialization failure, not every chat click.
        this.notice.hidden = true;
        this.closingPanels = true;
        try {
          await Promise.all([...document.querySelectorAll(NwpChatLaunchpoint.panelSelector)]
            .filter(panel => panel.hasAttribute('open') && typeof panel.hide === 'function')
            .map(panel => panel.hide()));
        } catch {
          this.closingPanels = false;
          this.unavailable();
          return;
        }
        this.closingPanels = false;
        if (!this.pending || !this.isConnected) return;
        if (this.openChat()) { this.settle(); this.notice.hidden = true; return; }
        const widgetLoading = [...document.scripts].some(script => {
          try { return new URL(script.src).hostname === 'code.tidio.co'; } catch { return false; }
        });
        if (!widgetLoading) { this.unavailable(); return; }
        this.timeout = window.setTimeout(() => {
          if (!this.isConnected) return;
          if (this.openChat()) { this.settle(); this.notice.hidden = true; }
          else this.unavailable();
        }, 4000);
      }

      unavailable() {
        this.settle();
        this.status.textContent = this.trigger.dataset.unavailableLabel;
        this.notice.hidden = false;
      }

      setExpanded(expanded) {
        this.expanded = expanded;
        if (!expanded && this.bodyLockObserver) this.finishBodyLock();
        this.trigger?.setAttribute('aria-expanded', String(expanded));
        if (this.trigger) this.trigger.setAttribute('aria-label', expanded ? this.trigger.dataset.closeLabel : this.trigger.dataset.openLabel);
      }

      settle() {
        window.clearTimeout(this.timeout);
        this.pending = false;
        this.pendingTrigger?.removeAttribute('aria-busy');
        if (this.pendingLabel) this.pendingLabel.textContent = this.originalLabel;
        this.pendingTrigger = this.pendingLabel = null;
        this.trigger?.removeAttribute('aria-busy');
        this.setExpanded(Boolean(this.expanded));
      }

      openChat() {
        this.bindApi();
        const api = window.tidioChatApi;
        try {
          if (!api || typeof api.open !== 'function' || typeof api.getStatus !== 'function' || api.getStatus() == null) return false;
          this.prepareBodyLock();
          if (typeof api.show === 'function') api.show();
          api.open();
          // An accepted command must remain reversible even when the provider
          // omits lifecycle events. Do not wait for animation completion.
          this.setExpanded(true);
          return true;
        } catch { this.finishBodyLock(); return false; }
      }

      closeChat() {
        this.bindApi();
        const api = window.tidioChatApi;
        try {
          if (typeof api?.close !== 'function') return false;
          api.close();
          this.setExpanded(false);
          return true;
        } catch { return false; }
      }
    }
    customElements.define('nwp-chat-launchpoint', NwpChatLaunchpoint);
  }
